import { computed, onBeforeMount, reactive, ref, toRefs } from 'vue'
import { apiRequest } from './http-service'
import { groupBy, map, uniq } from 'lodash'

export type EventName = 'add_to_calendar' | 'case_update_notification' | 'check_progress' | 'contact' | 'read_before_first_visit' | 'read_faq' | 'visit_page' | 'visit_case'

export interface GaEvent {
  timestamp: string
  keyCustomer: string
  requestStatus: string
  workTypeDescription: string
  name: EventName
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

const state = reactive({
  events: [] as GaEvent[],
  keyCustomerFilter: [] as string[],
  workTypeFilter: [] as string[],
  fromDate: '',
  toDate: ''
})

const distinctKeyCustomers = computed(() => {
  return uniq(state.events.map(e => e.keyCustomer))
})

const distinctWorkTypes = computed(() => {
  return uniq(state.events.map(e => e.workTypeDescription))
})

async function getEvents() {
  return apiRequest<{ events: GaEvent[], keyCustomers: string[] }>('GET', 'report/events')
}

const calculatePercentage = (events: GaEvent[], eventName: string) => {
  return Object.entries(groupBy(events.filter(e => e.name === eventName), e => e.timestamp.split('T')[0]))
    .map(([date, events]) => {
      const visitedCases = (visitedCasesByDay.value.find(([d]) => d === date)?.[1] || 0) as number
      const eventCount = events.length
      const percentage = visitedCases > 0 ? (eventCount / visitedCases) * 100 : 0
      return [date, parseFloat(percentage.toFixed(2))]
    })
}

const filteredEvents = computed(() => {
  return state.events?.filter(e => (!state.keyCustomerFilter.length || state.keyCustomerFilter.includes(e.keyCustomer)) &&
  (!state.workTypeFilter.length || state.workTypeFilter.includes(e.workTypeDescription)) &&
  (!state.fromDate || new Date(e.timestamp) >= new Date(state.fromDate)) &&
  (!state.toDate || new Date(e.timestamp) <= new Date(state.toDate))) || []
})

const sentSmsByDay = computed(() => {
  return Object.entries(filteredEvents.value.filter(e => e.name === 'case_update_notification' && e.sentSmsCount > 0)
    .reduce((acc, e) => {
      const day = e.timestamp.split('T')[0]
      acc[day] = (acc[day] || 0) + e.sentSmsCount
      return acc
    }, {} as Record<string, number>))
})

const sentSmsByCustomer = computed(() => {
  const data = Object.entries(groupBy(filteredEvents.value.filter(e => e.name === 'case_update_notification'), e => e.keyCustomer))
    .map(([keyCustomer, events]) => ({
      name: keyCustomer,
      value: events.reduce((acc, e) => acc + e.sentSmsCount, 0)
    }))

  const total = data.reduce((sum, item) => sum + item.value, 0)

  return {
    title: { text: 'SMS Amount' },
    tooltip: {
      trigger: 'item',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: function(params: any) {
        const percentage = ((params.value / total) * 100).toFixed(2)
        return `${params.name}: ${params.value} (${percentage}%)`
      }
    },
    series: {
      name: 'SMS Sent',
      type: 'pie',
      radius: '55%',
      data: data
    }
  }
})

const visitedCasesByDay = computed(() => {
  return map(groupBy(filteredEvents.value.filter(e => e.name === 'visit_case'), e => e.timestamp.split('T')[0]), (events, date) => [date, events.length])
})

const caseUpdateNotificationDistribution = computed(() => {
  return {
    title: { text: 'Case Update Notifications' },
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {d}%'
    },
    series: {
      name: 'Case Update Notification',
      type: 'pie',
      radius: '55%',
      data: Object.entries(groupBy(filteredEvents.value.filter(e => e.smsTemplateId), e => e.smsTemplateId))
        .map(([type, events]) => ({ name: type, value: events.length }))
    }
  }
})

const keyCustomers = computed(() => {
  return {
    title: { text: 'Event By Key Customer' },
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {d}%'
    },
    series: {
      name: 'Key Customer',
      type: 'pie',
      radius: '55%',
      data: Object.entries(groupBy(filteredEvents.value, e => e.keyCustomer))
        .map(([keyCustomer, events]) => ({ name: keyCustomer, value: events.length }))
    }
  }
})

const workTypeDistribution = computed(() => {
  return {
    title: { text: 'Work Types' },
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {d}%'
    },
    series: {
      name: 'Work Type',
      type: 'pie',
      radius: '55%',
      data: Object.entries(groupBy(filteredEvents.value, e => e.workTypeDescription))
        .map(([workType, events]) => ({ name: workType, value: events.length }))
    }
  }
})

const contactEvents = computed(() => {
  return {
    title: { text: 'Contact Events' },
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {d}%'
    },
    series: {
      name: 'Contact',
      type: 'pie',
      radius: '55%',
      data: Object.entries(groupBy(filteredEvents.value.filter(e => e.name === 'contact'), e => {
        if(e.channel === 'email' && e.role === 'project_manager') {
          return 'E-mail Project Manager'
        } else if(e.channel === 'email' && e.role === 'technician') {
          return 'E-mail Technician'
        } else if(e.channel === 'phone' && e.role === 'project_manager') {
          return 'Call Project Manager'
        } else if(e.channel === 'phone' && e.role === 'technician') {
          return 'Call Technician'
        }
      })).map(([channel, events]) => ({ name: channel, value: events.length }))
    }
  }
})

const contactEventsByDay = computed(() => {
  const contactProjectManagerByEmail = calculatePercentage(filteredEvents.value.filter(e => e.name === 'contact' && e.channel === 'email' && e.role === 'project_manager'), 'contact')
  const contactProjectManagerByPhone = calculatePercentage(filteredEvents.value.filter(e => e.name === 'contact' && e.channel === 'phone' && e.role === 'project_manager'), 'contact')
  const contactTechnicianByEmail = calculatePercentage(filteredEvents.value.filter(e => e.name === 'contact' && e.channel === 'email' && e.role === 'technician'), 'contact')
  const contactTechnicianByPhone = calculatePercentage(filteredEvents.value.filter(e => e.name === 'contact' && e.channel === 'phone' && e.role === 'technician'), 'contact')

  return {
    title: { text: 'Contact Events' },
    xAxis: {
      type: 'time'
    },
    tooltip: {
      trigger: 'axis'
    },
    yAxis: {
      axisLabel: {
        formatter: '{value}%'
      }
    },
    legend: {
      data: ['Project Manager E-mail', 'Project Manager Phone', 'Technician E-mail', 'Technician Phone']
    },
    series: [
      {
        name: 'Project Manager E-mail',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: contactProjectManagerByEmail
      },
      {
        name: 'Project Manager Phone',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: contactProjectManagerByPhone
      },
      {
        name: 'Technician E-mail',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: contactTechnicianByEmail
      },
      {
        name: 'Technician Phone',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: contactTechnicianByPhone
      }
    ]

  }
})

const checkedProgressPerDay = computed(() => {
  const progressCheckData = calculatePercentage(filteredEvents.value, 'check_progress')
  const readBeforeFirstVisit = calculatePercentage(filteredEvents.value, 'read_before_first_visit')
  const addToCalendar = calculatePercentage(filteredEvents.value, 'add_to_calendar')

  return {
    title: { text: 'User Interactions' },
    xAxis: {
      type: 'time'
    },
    tooltip: {
      trigger: 'axis'
    },
    yAxis: {
      axisLabel: {
        formatter: '{value}%'
      }
    },
    legend: {
      data: ['Check Progress', 'Read Before First Visit', 'Add To Calendar']
    },
    series: [
      {
        name: 'Check Progress',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: progressCheckData
      },
      {
        name: 'Read Before First Visit',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: readBeforeFirstVisit
      },
      {
        name: 'Add To Calendar',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: addToCalendar
      }
    ]
  }
})

const frequentlyAskedQuestions = computed(() => {
  const groupedFaqs = groupBy(filteredEvents.value.filter(e => e.name === 'read_faq'), e => e.title)
  return {
    title: { text: 'Frequently Asked Questions Clicked' },
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {d}%'
    },
    series:
      {
        name: 'FAQ',
        type: 'pie',
        radius: '55%',
        data: Object.entries(groupedFaqs).map(([title, events]) => ({ name: title, value: events.length }))
      }
  }
})

const smsOverTime = computed(() => {
  return {
    title: { text: 'Case Activity' },
    legend: {
      data: ['SMS Sent', 'Cases Visited']
    },
    xAxis: {
      type: 'time'
    },
    tooltip: {
      trigger: 'axis'
    },
    yAxis: {},
    series: [
      {
        name: 'SMS Sent',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: sentSmsByDay.value
      },
      {
        name: 'Cases Visited',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: visitedCasesByDay.value
      }
    ]
  }
})

export function useAnalyticsEvents() {
  const loading = ref(false)
  onBeforeMount(async () => {
    try {
      loading.value = true
      const { events } = await getEvents()
      state.events = events
    } finally {
      loading.value = false
    }
  })
  return {
    loading,
    frequentlyAskedQuestions,
    smsOverTime,
    contactEvents,
    visitedCasesByDay,
    checkedProgressPerDay,
    contactEventsByDay,
    workTypeDistribution,
    keyCustomers,
    caseUpdateNotificationDistribution,
    distinctKeyCustomers,
    distinctWorkTypes,
    sentSmsByCustomer,
    ...toRefs(state)
  }
}

export { getEvents }
